import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "../lib/api";
import { STATUS_STYLE, fmtDate, parseShiftTime } from "../lib/schedule";
import { useShifts } from "../context/ShiftsContext";
import { Plus, PlaneTakeoff, Check, X, Inbox, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { EmptyState } from "./OpenShifts";
import { toast } from "sonner";

const TYPES = ["PTO", "Sick", "CME", "Unpaid"];

export default function TimeOff({ isAdmin }) {
  const { shifts: SHIFTS } = useShifts();
  const [reqs, setReqs] = useState([]);
  const [open, setOpen] = useState(false);
  const initTimes = parseShiftTime((SHIFTS[0] || {}).time || "");
  const [form, setForm] = useState({ start_date: "", end_date: "", type: "PTO", reason: "", shift: "1st", start_time: initTimes.start, end_time: initTimes.end });

  const load = useCallback(async () => {
    try { setReqs((await api.get("/time-off")).data); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onShiftChange = (v) => {
    const t = parseShiftTime((SHIFTS.find((s) => s.key === v) || {}).time || "");
    setForm((f) => ({ ...f, shift: v, start_time: t.start, end_time: t.end }));
  };

  const submit = async () => {
    if (!form.start_date || !form.end_date) return toast.error("Pick start and end dates");
    try {
      await api.post("/time-off", form);
      toast.success("Request submitted");
      setOpen(false);
      const t = parseShiftTime((SHIFTS[0] || {}).time || "");
      setForm({ start_date: "", end_date: "", type: "PTO", reason: "", shift: "1st", start_time: t.start, end_time: t.end });
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const decide = async (id, status) => {
    try { await api.put(`/time-off/${id}/status`, { status, admin_comment: "" }); toast.success(`Request ${status}`); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const postOpen = async (id) => {
    try {
      const res = await api.post(`/time-off/${id}/post-open`);
      toast.success(`Posted ${res.data.open_shifts_created} open shift${res.data.open_shifts_created === 1 ? "" : "s"} to pick up`);
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/time-off/${id}`); toast.success("Request deleted"); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const shiftLabel = (k) => (SHIFTS.find((s) => s.key === k) || SHIFTS[0]).label;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-display">Time Off</h1>
          <p className="text-sm text-slate-500 mt-1">{isAdmin ? "Review and approve staff time-off requests." : "Request PTO, sick or CME days."}</p>
        </div>
        {!isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="request-time-off-button" className="gap-2 bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" />Request Time Off</Button>
            </DialogTrigger>
            <DialogContent data-testid="request-time-off-dialog">
              <DialogHeader><DialogTitle>Request time off</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Start</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="time-off-start-input" /></div>
                  <div className="space-y-2"><Label>End</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="time-off-end-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="time-off-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Shift to cover</Label>
                    <Select value={form.shift} onValueChange={onShiftChange}>
                      <SelectTrigger data-testid="time-off-shift-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{SHIFTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>From time</Label>
                    <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="time-off-start-time-input" /></div>
                  <div className="space-y-2"><Label>To time</Label>
                    <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="time-off-end-time-input" /></div>
                </div>
                <div className="space-y-2"><Label>Reason</Label>
                  <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Optional note for your manager" data-testid="time-off-reason-input" /></div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="submit-time-off-button" className="bg-slate-900 hover:bg-slate-800">Submit Request</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {reqs.length === 0 ? (
        <EmptyState icon={PlaneTakeoff} text={isAdmin ? "No time-off requests yet." : "You have no time-off requests."} />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100">
          {reqs.map((r) => (
            <div key={r.id} data-testid={`time-off-row-${r.id}`} className="p-4 sm:p-5 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{r.type}</span>
                  <span className="text-xs font-mono uppercase tracking-wide text-slate-400">{r.user_name}</span>
                </div>
                <div className="text-sm text-slate-600 mt-0.5">{fmtDate(r.start_date)} → {fmtDate(r.end_date)} · {shiftLabel(r.shift)}{(r.start_time && r.end_time) ? ` (${r.start_time}–${r.end_time})` : ""}</div>
                {r.reason && <div className="text-sm text-slate-400 mt-0.5">{r.reason}</div>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${STATUS_STYLE[r.status]}`} data-testid={`time-off-status-${r.id}`}>{r.status}</span>
                {r.posted_open && (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-cyan-50 text-cyan-800 border-cyan-200" data-testid={`time-off-posted-${r.id}`}>In Open Shifts</span>
                )}
              </div>
              {isAdmin && (
                <div className="flex gap-2 flex-wrap">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => decide(r.id, "approved")} data-testid={`approve-time-off-${r.id}`} className="gap-1 bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4" />Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "rejected")} data-testid={`reject-time-off-${r.id}`} className="gap-1 text-rose-600"><X className="h-4 w-4" />Reject</Button>
                    </>
                  )}
                  {r.status === "approved" && !r.posted_open && (
                    <Button size="sm" onClick={() => postOpen(r.id)} data-testid={`post-open-time-off-${r.id}`} className="gap-1 bg-cyan-600 hover:bg-cyan-700"><Inbox className="h-4 w-4" />Post to Open Shifts</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => remove(r.id)} data-testid={`delete-time-off-${r.id}`} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
